import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';

/**
 * Route guard that allows navigation only when the user holds a valid,
 * non-expired JWT token.
 *
 * @remarks
 * Awaits {@link AuthService.initialized} before reading the signal so the
 * guard never fires before the stored session has been restored from secure
 * storage. Without this wait, a page reload would always redirect to `/auth`
 * because `restoreStoredToken()` is async and the signal starts as `null`.
 *
 * Redirects to `/auth` when the token is absent or expired.
 *
 * @example
 * ```ts
 * // In app.routes.ts
 * { path: 'tabs', loadChildren: ..., canActivate: [authGuard] }
 * ```
 */
export const authGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  await auth.initialized;
  return auth.isAuthenticated() ? true : router.parseUrl('/auth');
};
